/**
 * Multilingual episode titles.
 *
 * Storage format in JSON supports EITHER:
 *   - Plain string with " | " separator:
 *       "الحلقة 01 | Episode 01 | Épisode 01"
 *     (order = AR | EN | FR; missing parts fall back to the first non-empty one.)
 *   - Object shape:
 *       { ar: "الحلقة 01", en: "Episode 01", fr: "Épisode 01" }
 *
 * Both are accepted transparently by the loader/schema. This helper takes
 * whatever the JSON contains and returns the correct string for a given
 * UI language, with graceful fallback.
 */

export type LangCode = "ar" | "en" | "fr";

export type EpisodeTitleInput =
  | string
  | {
      ar?: string;
      en?: string;
      fr?: string;
    }
  | undefined
  | null;

export const EPISODE_TITLE_SEPARATOR = " | ";

/** Parse either a "ar | en | fr" pipe string or an {ar,en,fr} object. */
export function parseEpisodeTitle(input: EpisodeTitleInput): { ar: string; en: string; fr: string } {
  if (!input) return { ar: "", en: "", fr: "" };
  if (typeof input === "string") {
    const parts = input.split("|").map((s) => s.trim());
    const [ar = "", en = "", fr = ""] = parts;
    return { ar, en, fr };
  }
  return { ar: input.ar ?? "", en: input.en ?? "", fr: input.fr ?? "" };
}

/** Serialize the {ar,en,fr} object back into the pipe-separated string. */
export function serializeEpisodeTitle(t: { ar?: string; en?: string; fr?: string }): string {
  const ar = (t.ar ?? "").trim();
  const en = (t.en ?? "").trim();
  const fr = (t.fr ?? "").trim();
  return [ar, en, fr].join(EPISODE_TITLE_SEPARATOR).replace(/(\s*\|\s*)+$/g, "");
}

/** Pick the best title for the given language, falling back to any non-empty. */
export function localizeEpisodeTitle(input: EpisodeTitleInput, lang: LangCode): string {
  const { ar, en, fr } = parseEpisodeTitle(input);
  const first =
    lang === "ar" ? ar || en || fr :
    lang === "en" ? en || ar || fr :
                    fr || en || ar;
  return first || "";
}
